import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { cn } from '@/lib/utils';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

export interface UsersRolesPaginationProps {
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  onPageIndexChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  className?: string;
}

export const UsersRolesPagination = ({
  pageIndex,
  pageSize,
  totalCount,
  onPageIndexChange,
  onPageSizeChange,
  className,
}: UsersRolesPaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const start = totalCount === 0 ? 0 : pageIndex * pageSize + 1;
  const end = Math.min((pageIndex + 1) * pageSize, totalCount);

  return (
    <div
      className={cn(
        'flex shrink-0 flex-wrap items-center justify-between gap-4 border-t px-4 py-3 text-sm text-muted-foreground',
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap">Rows per page</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]" aria-label="Rows per page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="top">
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span>{`Showing ${start}–${end} of ${totalCount}`}</span>
      </div>

      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-muted-foreground"
          onClick={() => onPageIndexChange(pageIndex - 1)}
          disabled={pageIndex <= 0}
        >
          <CaretLeft className="mr-1 h-4 w-4" aria-hidden />
          Previous
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-muted-foreground"
          onClick={() => onPageIndexChange(pageIndex + 1)}
          disabled={pageIndex >= totalPages - 1}
        >
          Next
          <CaretRight className="ml-1 h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
};
